const MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024;

class SigmaContactForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('[data-contact-form]');
    this.fields = this.querySelector('[data-contact-fields]');
    this.input = this.querySelector('[data-contact-files]');
    this.list = this.querySelector('[data-contact-file-list]');
    this.status = this.querySelector('[data-contact-status]');
    this.error = this.querySelector('[data-contact-error]');
    this.submit = this.querySelector('[data-contact-submit]');
    this.submitLabel = this.submit.querySelector('[data-contact-submit-label]') || this.submit.querySelector('.button__content') || this.submit;
    this.success = this.querySelector('[data-contact-success]');
    this.warranty = this.dataset.kind === 'warranty';
    this.allowedTypes = new Set(this.input.accept.split(','));
    this.unavailable = this.dataset.unavailable === 'true';
    this.idleLabel = this.warranty ? 'Submit request' : 'Send message';
    const purchaseDate = this.form.elements.namedItem('purchaseDate');
    if (purchaseDate) {
      const today = new Date();
      purchaseDate.max = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }
    this.files = [];
    this.openedAt = new Date().toISOString();
    this.fields.disabled = false;
    this.submit.disabled = this.unavailable;
    this.form.noValidate = true;
    this.input.addEventListener('change', () => {
      this.addFiles([...this.input.files]);
      this.input.value = '';
    });
    const drop = this.querySelector('[data-contact-drop]');
    let dragDepth = 0;
    drop.addEventListener('dragenter', (event) => {
      event.preventDefault();
      if (!this.locked && !this.busy) { dragDepth++; drop.dataset.dragging = 'true'; }
    });
    drop.addEventListener('dragover', (event) => event.preventDefault());
    drop.addEventListener('dragleave', () => {
      if (--dragDepth <= 0) { dragDepth = 0; delete drop.dataset.dragging; }
    });
    drop.addEventListener('drop', (event) => {
      event.preventDefault();
      dragDepth = 0;
      delete drop.dataset.dragging;
      this.addFiles([...event.dataTransfer.files]);
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.send();
    });
    this.form.addEventListener('keydown', (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        this.form.requestSubmit();
      }
    });
    this.form.addEventListener('input', (event) => {
      event.target.removeAttribute('aria-invalid');
      if (event.target.name === 'mediaLink') event.target.setCustomValidity('');
      if (!this.locked) this.requestId = null;
    });
    this.querySelector('[data-contact-reset]').addEventListener('click', () => this.reset());
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  addFiles(incoming) {
    if (this.busy || this.locked) return;
    const errors = [];
    for (const file of incoming) {
      if (!this.allowedTypes.has(file.type)) { errors.push(`${file.name}: ${this.warranty ? 'choose a supported photo or video' : 'choose a supported photo, video or PDF'}.`); continue; }
      if (!file.size || file.size > MAX_BYTES) { errors.push(`${file.name}: choose a file between 1 byte and 10 MB.${this.warranty && file.size > MAX_BYTES ? ' For larger files, add a video sharing link below.' : ''}`); continue; }
      if (this.files.some((item) => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified)) continue;
      if (this.files.length >= MAX_FILES) { errors.push('You can attach up to 10 files. Remove a file before adding another.'); break; }
      this.files.push({ file, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });
    }
    this.requestId = null;
    this.showError(errors.join(' '));
    this.renderFiles();
    this.status.textContent = `${this.files.length} of 10 files selected. Files upload when you send your message.`;
  }

  renderFiles(focusIndex) {
    this.list.replaceChildren();
    this.files.forEach((item, index) => {
      const row = document.createElement('li');
      row.className = 'contact-page__file';
      if (item.preview) {
        const image = document.createElement('img');
        image.className = 'contact-page__file-preview';
        image.src = item.preview;
        image.alt = '';
        image.width = 48;
        image.height = 48;
        row.append(image);
      } else {
        const icon = document.createElement('span');
        icon.className = 'contact-page__file-preview contact-page__file-type';
        icon.textContent = item.file.type.startsWith('video/') ? 'VIDEO' : 'PDF';
        icon.setAttribute('aria-hidden', 'true');
        row.append(icon);
      }
      const details = document.createElement('div');
      details.className = 'contact-page__file-details';
      const name = document.createElement('bdi');
      name.className = 'contact-page__file-name';
      name.textContent = item.file.name;
      const size = document.createElement('span');
      size.className = 'contact-page__file-size';
      size.textContent = `${item.file.size < 1024 * 1024 ? `${Math.max(1, Math.round(item.file.size / 1024))} KB` : `${(item.file.size / 1024 / 1024).toFixed(1)} MB`} · ${item.uploaded ? 'Uploaded' : 'Ready to upload'}`;
      details.append(name, size);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'contact-page__file-remove';
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${item.file.name}`);
      remove.disabled = Boolean(this.locked || this.busy);
      remove.addEventListener('click', () => {
        if (item.preview) URL.revokeObjectURL(item.preview);
        this.files.splice(index, 1);
        this.requestId = null;
        this.showError('');
        this.renderFiles(Math.min(index, this.files.length - 1));
        this.status.textContent = `${item.file.name} removed. ${this.files.length} of 10 files selected.`;
      });
      row.append(details, remove);
      this.list.append(row);
    });
    if (focusIndex !== undefined) {
      (this.list.querySelectorAll('button')[focusIndex] || this.input).focus();
    }
  }

  async post(path, body) {
    const response = await fetch(`${this.dataset.endpoint}/${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(60000),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(result.code === 'send_outcome_unknown'
        ? 'Delivery could not be confirmed. Retry to check the same message without creating another.'
        : response.status === 429
        ? 'Too many attempts. Please wait a few minutes before trying again.'
        : response.status === 503
          ? 'The contact form is temporarily unavailable. Please email support@sigmacoffee.co.uk.'
          : 'Your message could not be confirmed. Please try again.');
      error.status = response.status;
      error.code = result.code;
      error.reference = result.reference;
      error.fileIndex = result.fileIndex;
      throw error;
    }
    return result;
  }

  async send() {
    if (this.busy || this.terminal || this.unavailable) return;
    this.showError('');
    if (!this.locked) {
      const mediaLink = this.form.elements.namedItem('mediaLink');
      if (mediaLink) {
        mediaLink.value = mediaLink.value.trim();
        mediaLink.setCustomValidity('');
        if (mediaLink.value) {
          try {
            const url = new URL(mediaLink.value);
            if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) throw new Error('Invalid sharing link');
          } catch {
            mediaLink.setCustomValidity('Enter a full http:// or https:// sharing link without login details.');
          }
        }
      }
      const invalid = [...this.form.elements].find((field) => field.willValidate && !field.checkValidity());
      if (invalid) {
        invalid.setAttribute('aria-invalid', 'true');
        this.showError(invalid.validity.valueMissing ? 'Please complete the required fields and confirm the terms where requested.' : invalid.type === 'email' ? 'Please enter a valid email address.' : invalid.type === 'url' ? 'Please enter a full http:// or https:// sharing link without login details.' : 'Please check the highlighted field.');
        invalid.reportValidity();
        invalid.scrollIntoView({ block: 'center' });
        invalid.focus({ preventScroll: true });
        return;
      }
      for (const name of (this.warranty ? ['name', 'message', 'orderNumber', 'item'] : ['name', 'message'])) {
        const field = this.form.elements.namedItem(name);
        if (!field.value.trim()) {
          field.setAttribute('aria-invalid', 'true');
          this.showError('Please complete the highlighted field.');
          field.scrollIntoView({ block: 'center' });
          field.focus({ preventScroll: true });
          return;
        }
      }
    }
    const values = new FormData(this.form);
    this.busy = true;
    this.fields.disabled = true;
    this.submit.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    this.submitLabel.textContent = 'Sending…';
    let phase = 'session';
    try {
      if (!this.session) {
        this.requestId ||= crypto.randomUUID();
        this.status.textContent = 'Preparing your message…';
        const payload = {
          submissionId: this.requestId,
          name: values.get('name').trim(),
          email: values.get('email').trim(),
          subject: values.get('subject'),
          message: values.get('message').trim(),
          website: values.get('website') || '',
          openedAt: this.openedAt,
          files: this.files.map(({ file }) => ({ name: file.name, size: file.size, type: file.type })),
        };
        if (this.warranty) {
          payload.kind = 'warranty';
          payload.warranty = Object.fromEntries(['goldCover', 'orderNumber', 'purchaseDate', 'item', 'serialNumber', 'message', 'packagingRetained', 'accessoriesRetained', 'mediaLink'].map((key) => [key, String(values.get(key) || '').trim()]));
          payload.warranty.termsAccepted = values.get('termsAccepted') === 'on';
          payload.warranty.termsVersion = this.dataset.termsVersion;
        }
        this.session = await this.post('session', payload);
        const validUploads = Array.isArray(this.session.uploads) && this.session.uploads.length === this.files.length && this.session.uploads.every((upload) => {
          if (!upload || typeof upload.url !== 'string' || upload.method !== 'PUT') return false;
          try {
            const url = new URL(upload.url);
            return url.protocol === 'https:' || (location.hostname === '127.0.0.1' && url.origin === location.origin);
          } catch { return false; }
        });
        if (typeof this.session.token !== 'string' || !this.session.token || this.session.submissionId !== this.requestId || !validUploads) {
          this.session = null;
          throw new Error('Unable to prepare your message. Please try again.');
        }
        this.locked = true;
      }
      phase = 'upload';
      for (const [index, item] of this.files.entries()) {
        if (item.uploaded) continue;
        this.status.textContent = `Uploading file ${index + 1} of ${this.files.length}…`;
        const upload = this.session.uploads[index];
        const response = await fetch(upload.url, {
          method: 'PUT', headers: upload.headers || { 'Content-Type': item.file.type },
          body: item.file, credentials: 'omit', signal: AbortSignal.timeout(120000),
        });
        if (!response.ok) {
          const error = new Error(`Unable to upload ${item.file.name}. Check your connection and retry.`);
          if ([400, 401, 403, 404, 410].includes(response.status)) error.code = 'upload_session_invalid';
          throw error;
        }
        item.uploaded = true;
        this.renderFiles();
      }
      phase = 'send';
      this.status.textContent = 'Sending your message…';
      const result = await this.post('send', { submissionId: this.session.submissionId, token: this.session.token });
      if (result.ok !== true || !result.reference) throw new Error('Your message could not be confirmed. Please retry to check its status.');
      this.form.hidden = true;
      this.success.hidden = false;
      this.querySelector('[data-contact-reference]').textContent = result.reference;
      this.status.textContent = '';
      this.success.focus();
      this.files.forEach(({ preview }) => { if (preview) URL.revokeObjectURL(preview); });
    } catch (error) {
      this.status.textContent = '';
      if (error.code === 'send_reconciliation_required') {
        this.terminal = true;
        error.message = `Delivery needs support review. Email support@sigmacoffee.co.uk and quote ${error.reference || this.requestId}. Please keep this page open and do not submit another message.`;
      }
      if (['session_expired_unsent', 'upload_session_invalid'].includes(error.code)) {
        this.session = null;
        this.requestId = null;
        this.locked = false;
        this.openedAt = new Date().toISOString();
        this.files.forEach((item) => { item.uploaded = false; });
        error.message = 'The upload session could not continue. Your message has not been sent and your details are kept here. Send again to start a new upload.';
      }
      if (['upload_missing', 'size_mismatch', 'type_mismatch', 'content_mismatch', 'finalization_failed'].includes(error.code)) {
        this.files.forEach((item) => { item.uploaded = false; });
        if (error.code !== 'upload_missing' && error.code !== 'finalization_failed') {
          this.session = null;
          this.requestId = null;
          this.locked = false;
          const filename = Number.isInteger(error.fileIndex) ? this.files[error.fileIndex]?.file.name : null;
          error.message = `${filename || 'An attachment'} could not be verified. Remove it and choose a supported ${this.warranty ? 'photo or video' : 'photo, video or PDF'} before sending again.`;
        } else {
          error.message = 'An attachment did not finish uploading. Retry to upload your files again.';
        }
        this.renderFiles();
      }
      const fallback = phase === 'send'
        ? 'Delivery could not be confirmed. Retry to check the same message without creating another.'
        : 'Unable to connect. Check your connection and try again.';
      this.showError(`${error.name === 'TimeoutError' || error.name === 'TypeError' ? fallback : error.message}${this.locked && !this.terminal ? ' Your message and files are kept here. Retry this submission, or email support@sigmacoffee.co.uk for help.' : ''}`);
    } finally {
      this.busy = false;
      this.fields.disabled = Boolean(this.locked);
      this.submit.disabled = Boolean(this.terminal);
      this.form.removeAttribute('aria-busy');
      this.submitLabel.textContent = this.terminal ? 'Contact support to confirm delivery' : this.locked ? 'Retry sending' : this.idleLabel;
      if (!this.form.hidden) this.renderFiles();
    }
  }

  reset() {
    this.files.forEach(({ preview }) => { if (preview) URL.revokeObjectURL(preview); });
    this.files = [];
    this.session = null;
    this.requestId = null;
    this.locked = false;
    this.terminal = false;
    this.openedAt = new Date().toISOString();
    this.form.reset();
    this.fields.disabled = false;
    this.form.hidden = false;
    this.success.hidden = true;
    this.showError('');
    this.status.textContent = '';
    this.submitLabel.textContent = this.idleLabel;
    this.renderFiles();
    this.form.elements.namedItem('name').focus();
  }
}

if (!customElements.get('sigma-contact-form')) customElements.define('sigma-contact-form', SigmaContactForm);
